import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Tooltip2 } from '@evertrue/et-components';
import AutocompleteComponent from 'components/forms/Autocomplete';
import useProposalsSearch from 'hooks/use-proposals-search';
import { fetchFromGraphql } from 'entities/helpers/gqlCall';
import proposalQueries from 'apps/proposals/proposal-queries';

/**
 * Fetches a proposal by ID directly from the API
 */
const fetchProposalById = async (proposalId) => {
  try {
    const response = await fetchFromGraphql(proposalQueries.proposal, { proposalId }, 'proposal');
    return response?.data?.proposal || response?.proposal;
  } catch (error) {
    console.error('Error fetching proposal by ID:', error);
    return null;
  }
};

/**
 * Fetches all proposals for given contact IDs and finds the matching one
 */
const fetchProposalsByContact = async (contactIds, proposalId) => {
  try {
    const response = await fetchFromGraphql(
      proposalQueries.searchProposalsByContactId,
      { contact_ids: contactIds, title_search: null, limit: 100 },
      'searchProposalsByContactId'
    );
    const allProposals = response?.data?.searchProposalsByContactId || response?.searchProposalsByContactId || [];
    return allProposals.find((p) => p.id === proposalId);
  } catch (error) {
    console.error('Error fetching proposals by contact:', error);
    return null;
  }
};

const RelatedProposalField = ({
  id,
  onChange,
  onBlur,
  name,
  value,
  required = false,
  field,
  meta,
  helpers,
  contactId,
  contactIds,
  disabled = false,
}) => {
  const contactIdsToUse = useMemo(() => {
    return contactIds || (contactId ? [contactId] : []);
  }, [contactId, contactIds]);

  const { proposals, loading, query, handleSearch, clearSearch } = useProposalsSearch(contactIdsToUse);
  const [selectedProposal, setSelectedProposal] = useState(null);

  useEffect(() => {
    const fetchInitialProposal = async () => {
      // No value means clear the field
      if (!value) {
        setSelectedProposal(null);
        return;
      }

      // Check if proposal is already in search results
      const proposalInResults = proposals.find((p) => p.value === value);
      if (proposalInResults) {
        setSelectedProposal(proposalInResults);
        return;
      }

      // If disabled (coming from proposal page) and no contact selected, fetch directly by ID
      if (disabled && contactIdsToUse.length === 0) {
        const proposal = await fetchProposalById(value);
        if (proposal) setSelectedProposal({ ...proposal, value: proposal.id });
        return;
      }

      // If no contacts and not disabled, can't search
      if (contactIdsToUse.length === 0) {
        setSelectedProposal(null);
        return;
      }

      // Search for proposal in contact's proposals
      const proposal = await fetchProposalsByContact(contactIdsToUse, value);

      if (proposal) {
        setSelectedProposal({ ...proposal, value: proposal.id });
      } else if (disabled) {
        // If not found and field is disabled (from proposal page), fetch directly by ID
        const fetchedProposal = await fetchProposalById(value);
        if (fetchedProposal) setSelectedProposal({ ...fetchedProposal, value: fetchedProposal.id });
      }
    };

    fetchInitialProposal();
  }, [value, contactIdsToUse, disabled, proposals]);

  const handleSelect = (selectedValue) => {
    setSelectedProposal(selectedValue);
    if (onChange) {
      onChange({
        target: { name, value: selectedValue?.value || null },
        currentTarget: { name, value: selectedValue?.value || null },
      });
    }
  };

  const autocompleteField = (
    <AutocompleteComponent
      value={selectedProposal}
      onSelect={handleSelect}
      placeholder={`Search for a related proposal ${required ? '' : '(optional)'}...`}
      searchResults={proposals}
      isSearching={loading}
      query={query}
      handleSearch={handleSearch}
      clearSearch={clearSearch}
      disabled={disabled}
    />
  );

  if (disabled) {
    return (
      <Tooltip2
        trigger={<div style={{ cursor: 'not-allowed' }}>{autocompleteField}</div>}
        style={{ cursor: 'not-allowed' }}
      >
        This interaction is already associated with a proposal.
      </Tooltip2>
    );
  }

  return <div>{autocompleteField}</div>;
};

RelatedProposalField.propTypes = {
  id: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  required: PropTypes.bool,
  contactId: PropTypes.number,
  contactIds: PropTypes.arrayOf(PropTypes.number),
  disabled: PropTypes.bool,
  field: PropTypes.shape({
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    value: PropTypes.any,
    checked: PropTypes.bool,
    multiple: PropTypes.bool,
  }),
  meta: PropTypes.shape({
    error: PropTypes.string,
    initialError: PropTypes.string,
    initialTouched: PropTypes.bool,
    initialValue: PropTypes.any,
    touched: PropTypes.bool,
    value: PropTypes.any,
  }),
  helpers: PropTypes.shape({
    setValue: PropTypes.func,
    setTouched: PropTypes.func,
    setError: PropTypes.func,
  }),
};

export default RelatedProposalField;
